import React from 'react';
import { Shield, User, Search, CreditCard, FileText, Lock, Cookie, Key, Database, ExternalLink, AlertCircle } from 'lucide-react';
import { COMPANY_CONFIG } from '../config/config';

const PrivacyPolicy = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const sections = [
    {
      icon: Shield,
      title: 'Information We Collect',
      content: 'We may collect the following types of information:',
      subsections: [
        {
          number: '1.1',
          title: 'Personal Information',
          items: [
            'Full name',
            'Contact number',
            'Email address',
            'NIC / ID details (when required for verification)',
            'Billing and payment information'
          ]
        },
        {
          number: '1.2',
          title: 'Usage Information',
          items: [
            'Login details',
            'Device information',
            'IP address',
            'Browsing activity',
            'Cookies and analytical data'
          ]
        },
        {
          number: '1.3',
          title: 'Booking Information',
          items: [
            'Travel dates',
            'Selected service provider',
            'Payment records',
            'E-ticket details'
          ]
        }
      ]
    },
    {
      icon: FileText,
      title: 'How We Use Your Information',
      content: 'We use your information for the following purposes:',
      list: [
        'To process bookings and issue e-tickets',
        'To communicate booking confirmations and updates',
        'To provide customer support',
        'To improve our website, mobile app, and services',
        'To comply with legal and regulatory requirements',
        'To prevent fraud, unauthorised access, and misuse'
      ]
    },
    {
      icon: User,
      title: 'Sharing of Information',
      content: 'We may share your information only in the following situations:',
      subsections: [
        {
          number: '3.1',
          title: 'With Service Providers',
          content: 'For fulfilling your booking (ex: vehicle operators, transport service providers).'
        },
        {
          number: '3.2',
          title: 'With Third-Party Platforms',
          content: 'Only when required for:',
          items: [
            'Payment processing',
            'SMS/email notifications',
            'System security and analytics'
          ]
        },
        {
          number: '3.3',
          title: 'Legal Requirements',
          content: 'We may disclose information if required by:',
          items: [
            'Law enforcement',
            'Court orders',
            'Regulatory authorities'
          ]
        }
      ],
      note: 'We do not sell or rent your personal information to any third party.'
    },
    {
      icon: Lock,
      title: 'Data Security',
      content: 'We take reasonable technical and organizational measures to protect your data, including:',
      list: [
        'Secure servers',
        'Encrypted payment gateways',
        'Limited access to sensitive information'
      ],
      note: 'However, no method of data transmission is 100% secure, and we cannot guarantee absolute security.'
    },
    {
      icon: Cookie,
      title: 'Cookies and Tracking Technologies',
      content: 'Our website and mobile app may use cookies, analytics tools, and similar technologies to:',
      list: [
        'Improve user experience',
        'Analyze traffic',
        'Remember user preferences'
      ],
      note: 'You can disable cookies in your browser settings, but some features may not function properly.'
    },
    {
      icon: Key,
      title: 'Your Rights',
      content: 'You may request:',
      list: [
        'Access to your personal information',
        'Correction of inaccurate details',
        'Deletion of information (subject to legal requirements)'
      ],
      note: 'To submit a request, please contact our office.'
    },
    {
      icon: Database,
      title: 'Data Retention',
      content: 'We retain personal information only for:',
      list: [
        'As long as necessary to complete bookings',
        'As required by law',
        'For internal record-keeping'
      ],
      note: 'After this period, data is securely deleted.'
    },
    {
      icon: ExternalLink,
      title: 'Third-Party Links',
      content: 'Our platform may contain links to third-party websites. We are not responsible for the privacy practices or content of those websites.'
    },
    {
      icon: AlertCircle,
      title: 'Changes to This Policy',
      content: 'We may update this Privacy Policy from time to time. Updated versions will be posted on our website or app with the revised date.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section - Very Compact */}
      <section className="w-screen min-w-screen max-w-screen bg-gradient-to-r from-blue-600 to-blue-800 text-white py-2 lg:py-3">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-1">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-1">Privacy Policy</h1>
            <p className="text-xs sm:text-sm text-blue-100">{COMPANY_CONFIG.appname}</p>
            <p className="text-xs text-blue-200 mt-1">Operated by Visual Soft</p>
           {/* <p className="text-xs text-blue-200 mt-1">Last Updated: {currentDate}</p>*/}
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed text-lg">
              {COMPANY_CONFIG.appname} ("we", "us", "our") is committed to protecting your privacy.
              This Privacy Policy explains how we collect, use, store, and protect your personal information 
              when you use our website, mobile application, or any services provided by us.
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {sections.map((section, sectionIndex) => {
              const Icon = section.icon;
              return (
                <div key={sectionIndex} className="bg-white rounded-xl shadow-md p-8 hover:shadow-lg transition-shadow">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4">
                        {sectionIndex + 1}. {section.title}
                      </h2>
                      
                      {section.content && (
                        <p className="text-gray-700 leading-relaxed mb-4">
                          {section.content}
                        </p>
                      )}

                      {/* Subsections with items */}
                      {section.subsections && (
                        <div className="space-y-6">
                          {section.subsections.map((subsection, subIndex) => (
                            <div key={subIndex} className="ml-4">
                              <div className="flex items-start space-x-3 mb-2">
                                <span className="text-blue-600 font-semibold flex-shrink-0">
                                  {subsection.number}
                                </span>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {subsection.title}
                                </h3>
                              </div>
                              {subsection.content && (
                                <p className="text-gray-700 leading-relaxed ml-7 mb-2">
                                  {subsection.content}
                                </p>
                              )}
                              {subsection.items && (
                                <ul className="list-disc list-inside space-y-1 ml-7 text-gray-700">
                                  {subsection.items.map((item, itemIndex) => (
                                    <li key={itemIndex} className="leading-relaxed">
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* List items */}
                      {section.list && !section.subsections && (
                        <ul className="list-disc list-inside space-y-2 ml-4 text-gray-700">
                          {section.list.map((item, itemIndex) => (
                            <li key={itemIndex} className="leading-relaxed">
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Note */}
                      {section.note && (
                        <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r">
                          <p className="text-gray-700 leading-relaxed italic">
                            {section.note}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Questions About Our Privacy Policy?
            </h3>
            <p className="text-gray-600 mb-4">
              If you have questions about how we handle your personal information or wish to exercise your rights, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Contact Us
              </a>
              <a
                href="/terms"
                className="inline-block bg-white text-blue-600 border-2 border-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors"
              >
                View Terms & Conditions
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;

